import React, { useState, useEffect } from 'react';
import { loadsService, featureFlagsService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Package, 
  Calculator, 
  AlertTriangle,
  RefreshCw,
  DollarSign,
  TrendingUp
} from 'lucide-react';

const LoadManagement = () => {
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('entry');
  const [formData, setFormData] = useState({
    reference_number: '',
    shipper_name: '',
    shipper_city: '',
    shipper_state: '',
    shipper_zip: '',
    consignee_name: '',
    consignee_city: '',
    consignee_state: '',
    consignee_zip: '',
    pickup_date: '',
    delivery_date: '',
    rate: '',
    miles: '',
    commodity: 'General Freight',
    equipment_type: 'dry_van'
  });
  const [profitCalc, setProfitCalc] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);

  useEffect(() => {
    checkFeature();
  }, []);

  const checkFeature = async () => {
    try {
      const enabledFeatures = await featureFlagsService.getEnabledFeatures();
      setFeatureEnabled(enabledFeatures.manual_load_entry_enabled === true);
    } catch (err) {
      setFeatureEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await loadsService.createManualLoad({
        ...formData,
        rate: parseFloat(formData.rate),
        miles: parseFloat(formData.miles)
      });
      alert('Load created successfully!');
      setFormData({
        reference_number: '',
        shipper_name: '',
        shipper_city: '',
        shipper_state: '',
        shipper_zip: '',
        consignee_name: '',
        consignee_city: '',
        consignee_state: '',
        consignee_zip: '',
        pickup_date: '',
        delivery_date: '',
        rate: '',
        miles: '',
        commodity: 'General Freight',
        equipment_type: 'dry_van'
      });
    } catch (err) {
      alert(err.response?.data?.detail?.message || 'Failed to create load');
    }
  };

  const calculateProfit = async () => {
    if (!formData.rate || !formData.miles) {
      alert('Please enter rate and miles');
      return;
    }
    try {
      setCalcLoading(true);
      const result = await loadsService.calculateProfit({
        revenue: parseFloat(formData.rate),
        miles: parseFloat(formData.miles),
        fuel_price_per_gallon: 3.50,
        mpg: 6.5,
        driver_pay_type: 'percentage',
        driver_pay_value: 25
      });
      setProfitCalc(result);
    } catch (err) {
      alert(err.response?.data?.detail?.message || 'Failed to calculate profit');
    } finally {
      setCalcLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto p-6">
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 text-yellow-600 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold text-yellow-800">Feature Not Enabled</h2>
                  <p className="text-yellow-700 mt-2">
                    The Load Management feature is currently disabled. Contact your administrator to enable this feature.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Load Management</h1>
          <p className="text-gray-600 mt-1">Create and manage freight loads</p>
        </div>

        <div className="flex border-b mb-6">
          {['entry', 'calculator'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize ${
                activeTab === tab 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab === 'entry' ? 'Load Entry' : 'Profit Calculator'}
            </button>
          ))}
        </div>

        {activeTab === 'entry' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" /> Manual Load Entry
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <Label>Reference Number *</Label>
                    <Input
                      name="reference_number"
                      value={formData.reference_number}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div>
                    <Label>Equipment Type</Label>
                    <select
                      name="equipment_type"
                      value={formData.equipment_type}
                      onChange={handleInputChange}
                      className="w-full border rounded-md p-2"
                    >
                      <option value="dry_van">Dry Van</option>
                      <option value="reefer">Reefer</option>
                      <option value="flatbed">Flatbed</option>
                      <option value="step_deck">Step Deck</option>
                    </select>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Origin (Shipper)</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Shipper Name *</Label>
                      <Input name="shipper_name" value={formData.shipper_name} onChange={handleInputChange} required />
                    </div>
                    <div>
                      <Label>City *</Label>
                      <Input name="shipper_city" value={formData.shipper_city} onChange={handleInputChange} required />
                    </div>
                    <div>
                      <Label>State *</Label>
                      <Input name="shipper_state" value={formData.shipper_state} onChange={handleInputChange} maxLength={2} required />
                    </div>
                    <div>
                      <Label>ZIP *</Label>
                      <Input name="shipper_zip" value={formData.shipper_zip} onChange={handleInputChange} required />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Destination (Consignee)</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Consignee Name *</Label>
                      <Input name="consignee_name" value={formData.consignee_name} onChange={handleInputChange} required />
                    </div>
                    <div>
                      <Label>City *</Label>
                      <Input name="consignee_city" value={formData.consignee_city} onChange={handleInputChange} required />
                    </div>
                    <div>
                      <Label>State *</Label>
                      <Input name="consignee_state" value={formData.consignee_state} onChange={handleInputChange} maxLength={2} required />
                    </div>
                    <div>
                      <Label>ZIP *</Label>
                      <Input name="consignee_zip" value={formData.consignee_zip} onChange={handleInputChange} required />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Schedule & Rate</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label>Pickup Date *</Label>
                      <Input type="date" name="pickup_date" value={formData.pickup_date} onChange={handleInputChange} required />
                    </div>
                    <div>
                      <Label>Delivery Date *</Label>
                      <Input type="date" name="delivery_date" value={formData.delivery_date} onChange={handleInputChange} required />
                    </div>
                    <div>
                      <Label>Rate ($) *</Label>
                      <Input type="number" name="rate" value={formData.rate} onChange={handleInputChange} step="0.01" required />
                    </div>
                    <div>
                      <Label>Miles *</Label>
                      <Input type="number" name="miles" value={formData.miles} onChange={handleInputChange} required />
                    </div>
                  </div>
                </div>

                <Button type="submit" className="w-full">Create Load</Button>
              </form>
            </CardContent>
          </Card>
        )}

        {activeTab === 'calculator' && (
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="w-5 h-5" /> Profit Calculator
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Revenue ($)</Label>
                    <Input type="number" name="rate" value={formData.rate} onChange={handleInputChange} step="0.01" />
                  </div>
                  <div>
                    <Label>Miles</Label>
                    <Input type="number" name="miles" value={formData.miles} onChange={handleInputChange} />
                  </div>
                  <Button onClick={calculateProfit} disabled={calcLoading} className="w-full">
                    {calcLoading ? 'Calculating...' : 'Calculate Profit'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {profitCalc && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" /> Profit Estimate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Revenue</span>
                      <span className="font-medium text-green-600">${profitCalc.revenue?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Driver Pay (25%)</span>
                      <span className="text-red-600">-${profitCalc.driver_pay?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Fuel Cost</span>
                      <span className="text-red-600">-${profitCalc.fuel_cost?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="text-gray-600">Total Costs</span>
                      <span className="text-red-600">-${profitCalc.total_costs?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-3 bg-gray-50 rounded px-2">
                      <span className="font-semibold">Gross Profit</span>
                      <span className={`font-bold ${profitCalc.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${profitCalc.gross_profit?.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Margin</span>
                      <Badge className={profitCalc.profit_margin_percent >= 20 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {profitCalc.profit_margin_percent?.toFixed(1)}%
                      </Badge>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">Revenue/Mile</span>
                      <span>${profitCalc.revenue_per_mile?.toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default LoadManagement;
